import { createElement, useRef, useState } from 'react';
import { KeyValue } from '@mews/enums';
import { isAtMostMobile, withBreakpointRange } from '@optimus-web/breakpoints';
import { ANIMATION_DURATION, InputValue } from '@optimus-web/common';
import { useClickAway, withScrollAway } from '@optimus-web/helpers';
import { IconName } from '@optimus-web/icons';
import { createDateMask } from '@optimus-web/masks';
import { Drawer } from '../Drawer';
import { withFocusTrap } from '../hooks/useFocusTrap';
import { InputProps } from '../Input';
import { TextInput } from '../TextInput';
import { getBreakpointRangeValueFallback } from '../utils/breakpointsFallback';
import { isDescendant } from '../utils/dom';
import { BaseMonthPicker } from './BaseMonthPicker';
import OverlayedMonthPicker from './OverlayedMonthPicker';
import { DatePickerContainerElement } from './style';
import { MonthPickerProps, MonthPickerType } from './types';

const MaskedDateField = createDateMask(TextInput);
const HiddenInput = (props: InputProps) => createElement('input', { type: 'hidden', ...props });

export const MonthPickerFieldComponent = <D, M, Y>({
    dateFormatPattern = 'MM/yyyy',
    value: propValue,
    dateValue,
    getCalendarService,
    onFocus,
    onBlur,
    onChange,
    onDateChange,
    registerScrollAway,
    deregisterScrollAway,
    registerFocusTrap,
    deregisterFocusTrap,
    modifiers,
    monthDateNormalize,
    minDate,
    maxDate,
    breakpointRange,
    inline,
    readOnly,
    ...other
}: MonthPickerProps<D, M, Y>) => {
    const initialValue = propValue || (dateValue && getCalendarService().getDateString(dateValue)) || '';
    const [opened, setOpened] = useState(false);
    const [value, setValue] = useState(initialValue);
    const { registerClickAway, deregisterClickAway } = useClickAway();

    type PickerType = typeof BaseMonthPicker;
    const containerElementRef = useRef(null);
    const pickerElementRef = useRef<PickerType>(null);

    const closeIf = (condition: boolean) => condition && close();
    const openIf = (condition: boolean) => condition && open();

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        openIf(!opened && !readOnly);
        onFocus?.(e);
    };

    const getOverlayElement = () => (pickerElementRef?.current as $TSFixMe)?.getOverlayEl();
    const getCalendarElement = () => (pickerElementRef?.current as $TSFixMe)?.getCalendarEl();

    const handleBlur = () => {
        const roots = [containerElementRef.current, getOverlayElement()];
        const shouldClose = !roots.some(
            (root) => document.activeElement && root && isDescendant(document.activeElement, root)
        );

        if (shouldClose) {
            closeIf(opened);
            onBlur?.(undefined as unknown as React.FocusEvent<HTMLInputElement>);
        }
    };

    const registerBlurHandler = () => document.addEventListener('focus', handleBlur, true);
    const deregisterBlurHandler = () => document.removeEventListener('focus', handleBlur);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === KeyValue.Enter && opened) {
            e.stopPropagation();
            e.preventDefault();
            close();
        }
    };

    const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const calendar = getCalendarElement();
        if (calendar) {
            calendar.handleKeyUp(e);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement> | React.MouseEvent, newValue: InputValue) => {
        const textValue = newValue.toString();
        setValue(textValue);

        // we need a valid date string from a month/year string
        const dateString = getCalendarService().getDateStringFromMonthYearString(textValue, dateFormatPattern);

        if (!getCalendarService().isValidDateString(dateString)) return;

        const date = getCalendarService().getDateFromDateString(dateString);

        // call onChange only with the valid date string to prevent validation errors
        onChange?.(e, dateString);
        onDateChange?.(date);
    };

    const handleDateChange = (date: D) => {
        const newValue = getCalendarService().getDateString(date);
        setValue(newValue);
        onChange?.(undefined as unknown as React.ChangeEvent<HTMLInputElement>, newValue);
        onDateChange?.(date);
    };

    const handleDateClick = () => {
        if (opened) {
            close();
            onBlur?.(undefined as unknown as React.FocusEvent<HTMLInputElement>);
        }
    };

    const handleDateIconClick = () => {
        if (opened) {
            close();
        } else {
            openIf(!readOnly);
        }
    };

    const open = () => {
        if (!opened && !isAtMostMobile(getBreakpointRangeValueFallback(breakpointRange)) && !readOnly) {
            registerBlurHandler();
            window.setTimeout(() => {
                const options = {
                    handler: close,
                    roots: [
                        () => containerElementRef.current,
                        () => (pickerElementRef.current ? getOverlayElement() : null),
                    ],
                };
                registerScrollAway(options);
                registerClickAway(options);
                registerFocusTrap([containerElementRef.current, pickerElementRef.current ? getOverlayElement() : null]);
            }, ANIMATION_DURATION.fast);
        }
        setOpened(true);
    };

    const close = () => {
        setOpened(false);
        deregisterClickAway();
        deregisterScrollAway();
        deregisterBlurHandler();
        deregisterFocusTrap();
    };

    const anchorElementGetter = () => containerElementRef.current;

    const getNormalizedStringValue = (stringValue: string) => {
        if (getCalendarService().isValidDateString(stringValue)) {
            const date = getCalendarService().getDateFromDateString(stringValue);
            const month = getCalendarService().getMonthOfDate(date);
            return getCalendarService().getMonthYearString(month);
        }
        return stringValue;
    };

    const firstDateOfMonth = (stringValue: string) => {
        if (getCalendarService().isValidDateString(stringValue)) {
            const date = getCalendarService().getDateFromDateString(stringValue);
            const month = getCalendarService().getMonthOfDate(date);
            const firstDate = getCalendarService().getMonthFirstDate(month);
            return getCalendarService().toISOString(firstDate);
        }
        return stringValue;
    };

    const getActiveDate = () => {
        const stringValue = value.toString();

        if (getCalendarService().isValidDateString(stringValue)) {
            return getCalendarService().getDateFromDateString(stringValue);
        }

        // date can be MM/YYYY, so we need to normalize it to MM/DD/YYYY
        const normalizedStringValue = getNormalizedStringValue(stringValue);
        const dateString = getCalendarService().getDateStringFromMonthYearString(
            normalizedStringValue,
            dateFormatPattern
        );

        if (getCalendarService().isValidDateString(dateString)) {
            return getCalendarService().getDateFromDateString(dateString);
        }

        return '';
    };

    const datePickerContainerProps = {
        ref: containerElementRef,
    };

    const calendarProps = {
        impRef: pickerElementRef,
        anchorElement: anchorElementGetter,
        date: getActiveDate(),
        getCalendarService,
        modifiers,
        onDateClick: handleDateClick,
        onDateChange: handleDateChange,
        onKeyDown: handleKeyDown,
        opened,
        monthSelectorVisible: true,
        monthDateNormalize,
        minDate,
        maxDate,
        breakpointRange,
    };

    const inputProps = {
        ...other,
        value,
        name: `${other.name}.MonthPart`,
        dateValue: getNormalizedStringValue(value.toString()),
        onFocus: handleFocus,
        onKeyUp: handleKeyUp,
        onChange: handleChange,
        onKeyDown: handleKeyDown,
        dateFormatPattern,
        interactiveIcon: inline ? undefined : IconName.Calendar,
        interactiveIconHandler: handleDateIconClick,
        inline: false,
        readOnly,
    };

    const inputPropsNormalized = {
        name: other.name,
        value: firstDateOfMonth(value.toString()),
    };

    const monthPickerComponent = () => {
        if (isAtMostMobile(getBreakpointRangeValueFallback(breakpointRange))) {
            return (
                <Drawer open={opened} onClose={close}>
                    {/* @ts-expect-error $TSFixMeError: Property 'getOverlayEl' does not exist on type 'BaseMonthPickerProps<D, M, Y>'. */}
                    <BaseMonthPicker {...calendarProps} />
                </Drawer>
            );
        }
        return <OverlayedMonthPicker {...calendarProps} />;
    };

    return (
        <DatePickerContainerElement {...datePickerContainerProps}>
            <MaskedDateField {...inputProps} />
            {!inline ? monthPickerComponent() : null}
            <HiddenInput {...inputPropsNormalized} />
        </DatePickerContainerElement>
    );
};

export const MonthPicker = withBreakpointRange(
    withFocusTrap(withScrollAway(MonthPickerFieldComponent))
) as unknown as MonthPickerType;
